import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Download, Users, Target, Briefcase, GraduationCap, Lock, Award } from "lucide-react";
import ChartSummary from "@/components/ChartSummary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIE_COLORS = ["#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"];

function DistrictSnapshot() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    try {
      const res = await fetch(`${API}/snapshot/${token}`);
      if (res.status === 404) {
        setError("This snapshot link does not exist.");
        return;
      }
      if (res.status === 410) {
        // Distinguish between revoked vs expired
        try {
          const j = await res.json();
          setError(j.detail || "This snapshot link is no longer available.");
        } catch (e) {
          setError("This snapshot link is no longer available.");
        }
        return;
      }
      if (!res.ok) {
        setError("Unable to load snapshot.");
        return;
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/snapshot/${token}/csv`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `district_snapshot_${token.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (e) { /* ignore */ }
    finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center" data-testid="snapshot-error">
          <Lock className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Snapshot Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const rollups = data.rollups || {};
  const ideaCatData = Object.entries(rollups.idea_categories || {}).map(([name, value]) => ({ name, value }));
  const profileDistData = Object.entries(rollups.profile_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: { D: "Builder", I: "Promoter", S: "Helper", C: "Strategist" }[k] || k,
      value: v,
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Branded Header for Presentation */}
      <header className="bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-600 text-white py-8 shadow-lg" data-testid="snapshot-header">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-80 mb-1">
                District Snapshot — Lemonade Stand Business Plan
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold" data-testid="snapshot-label">
                {data.snapshot?.label || "CTE Outcomes Report"}
              </h1>
              {(data.snapshot?.class_name || data.snapshot?.educator_name) && (
                <div className="text-sm opacity-90 mt-1">
                  {data.snapshot.class_name && <>📚 {data.snapshot.class_name}</>}
                  {data.snapshot.class_name && data.snapshot.educator_name && <> · </>}
                  {data.snapshot.educator_name && <>Educator: {data.snapshot.educator_name}</>}
                </div>
              )}
              {data.snapshot?.expires_at && (
                <div
                  className="mt-3 inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 px-3 py-1.5 rounded-full text-sm font-semibold"
                  data-testid="snapshot-validity"
                >
                  ✅ Valid through{" "}
                  {new Date(data.snapshot.expires_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-xs opacity-80">Read-only · Token-protected</div>
              <div className="text-xs opacity-80">
                Standards: <strong>Nevada CTE / Perkins V</strong> + <strong>CCTC</strong>
              </div>
              {data.generated_at && (
                <div className="text-xs opacity-70">
                  Generated {new Date(data.generated_at).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {data.student_count === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-5xl mb-4">📊</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">No data yet</h2>
            <p className="text-gray-600">Once students start completing Lesson 1, outcomes will appear here.</p>
          </div>
        ) : (
          <>
            {/* CSV download bar */}
            <div className="bg-white rounded-2xl shadow-md p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm text-gray-700">
                <strong>For Perkins V / CLNA reporting:</strong> download the structured CSV with dual-tagged standards.
              </div>
              <button
                onClick={downloadCSV}
                disabled={downloading}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:opacity-50"
                data-testid="snapshot-download-csv-btn"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Downloading..." : "Download CSV (Perkins-ready)"}
              </button>
            </div>

            {/* KPI cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI icon={<Users className="w-5 h-5" />} label="Students" value={data.student_count} color="from-blue-500 to-indigo-600" testId="snap-kpi-students" />
              <KPI icon={<Target className="w-5 h-5" />} label="Lesson 1 Completion" value={`${rollups.completion?.percent || 0}%`} sub={`${rollups.completion?.completed || 0} of ${data.student_count} completed`} color="from-emerald-500 to-teal-600" testId="snap-kpi-completion" />
              <KPI icon={<Award className="w-5 h-5" />} label="Skill Areas Engaged" value={(rollups.skills || []).length} sub="ES.1.1 – ES.7.1" color="from-orange-500 to-pink-500" testId="snap-kpi-skills" />
              <KPI icon={<GraduationCap className="w-5 h-5" />} label="Academic Subjects" value={Object.keys(rollups.academic_subjects || {}).length} sub="Cross-curricular reach" color="from-purple-500 to-fuchsia-600" testId="snap-kpi-subjects" />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <Card title="Top Skill Areas (engagement)" testId="snap-skill-chart">
                <ChartSummary
                  testId="snap-skill-chart-summary"
                  title="Top Skill Areas (engagement)"
                  items={(rollups.skills || []).map(s => ({ label: s.title, value: s.engagement }))}
                />
                {(rollups.skills || []).length === 0 ? (
                  <NoData />
                ) : (
                  <div role="img" aria-label={`Bar chart of top ${(rollups.skills||[]).length} skill areas by engagement`}>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={rollups.skills || []} layout="vertical" margin={{ left: 30 }}>
                        <CartesianGrid stroke="#f0f0f0" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="title" width={150} />
                        <Tooltip />
                        <Bar dataKey="engagement" fill="#6366F1" radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <Card title="Business Idea Categories" testId="snap-idea-chart">
                <ChartSummary
                  testId="snap-idea-chart-summary"
                  title="Business Idea Categories"
                  items={ideaCatData.map(d => ({ label: d.name, value: d.value }))}
                />
                {ideaCatData.length === 0 ? (
                  <NoData />
                ) : (
                  <div role="img" aria-label={`Pie chart of ${ideaCatData.length} business idea categories`}>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={ideaCatData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label>
                          {ideaCatData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              <Card title="Entrepreneur Profile Distribution" testId="snap-profile-chart">
                <ChartSummary
                  testId="snap-profile-chart-summary"
                  title="Entrepreneur Profile Distribution"
                  items={profileDistData.map(d => ({ label: d.name, value: d.value }))}
                />
                {profileDistData.length === 0 ? (
                  <NoData />
                ) : (
                  <div role="img" aria-label="Bar chart of entrepreneur profile distribution">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={profileDistData}>
                        <CartesianGrid stroke="#f0f0f0" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#EC4899" radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <Card title="Career Pathway Distribution" testId="snap-pathways-list">
                {Object.keys(rollups.career_pathways || {}).length === 0 ? (
                  <NoData />
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(rollups.career_pathways || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([name, count]) => (
                        <li key={name} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1">
                          <span className="text-gray-700">
                            <Briefcase className="w-3 h-3 inline mr-1 text-emerald-600" />
                            {name}
                          </span>
                          <span className="font-bold text-emerald-700">{count}</span>
                        </li>
                      ))}
                  </ul>
                )}
              </Card>
            </div>

            {/* Academic subjects + at-a-glance */}
            <Card title="Academic Subject Coverage" testId="snap-subjects-card">
              {Object.keys(rollups.academic_subjects || {}).length === 0 ? (
                <NoData />
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Lesson 1 outputs cross-map to these academic subjects (per Nevada CTE-for-credit pathways):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(rollups.academic_subjects || {}).map(([subj, n]) => (
                      <span
                        key={subj}
                        className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-800 text-sm font-semibold px-3 py-1 rounded-full"
                        data-testid={`snap-subject-${subj}`}
                      >
                        {subj}
                        <span className="bg-indigo-200 text-indigo-900 px-1.5 py-0.5 text-xs rounded-full">
                          {n}
                        </span>
                      </span>
                    ))}
                  </div>
                </>
              )}
            </Card>
          </>
        )}

        {/* Footer */}
        <footer className="text-center mt-10 pb-4 text-xs text-gray-400">
          <Lock className="w-3 h-3 inline mr-1" /> Token-protected · Read-only · No personally identifiable information beyond first names.
          <br />
          Standards-aligned with <strong>Nevada CTE / Perkins V</strong> and <strong>Common Career Technical Core</strong>.
        </footer>
      </main>
    </div>
  );
}

function KPI({ icon, label, value, sub, color, testId }) {
  return (
    <div className={`bg-gradient-to-br ${color} rounded-2xl shadow-lg p-5 text-white`} data-testid={testId}>
      <div className="flex items-center gap-2 opacity-90 text-sm mb-1">{icon}{label}</div>
      <div className="text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs opacity-90 mt-1">{sub}</div>}
    </div>
  );
}

function Card({ title, testId, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6" data-testid={testId}>
      <h3 className="font-bold text-gray-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function NoData() {
  return <p className="text-sm text-gray-500">No data yet.</p>;
}

export default DistrictSnapshot;
